/**
 * Loudness measurement, to ITU-R BS.1770-4 and EBU R128.
 *
 * A peak meter says how close a mix is to clipping. It says nothing about how
 * loud it *sounds*, which is why two masters that both peak at -0.3 dBFS can
 * differ by ten decibels to the ear. LUFS is the standard answer: filter the
 * audio the way hearing weights frequency, take the mean square in overlapping
 * blocks, and throw away the quiet parts before averaging so that silence
 * between phrases does not drag the number down.
 *
 * Three figures come out of it, and they answer different questions:
 *
 * - **Integrated** loudness, in LUFS: how loud the whole thing is. This is what
 *   streaming services normalise to, and what "-14 LUFS" or "-23 LUFS" means.
 * - **Loudness range** (LRA), in LU: how much the loudness varies across the
 *   piece. A large number means dynamics; a very small one means it has been
 *   flattened.
 * - **True peak**, in dBTP: the highest value the waveform reaches *between*
 *   samples, which is what a converter or an encoder will actually have to
 *   reproduce. A file can sit at -0.1 dBFS by sample and still clip.
 *
 * Everything here is pure and offline: it takes samples and returns numbers.
 */

/** The offset in the loudness formula, from BS.1770. */
const LOUDNESS_OFFSET = -0.691;

/** Blocks are 400ms, overlapping by 75%, which is what the standard specifies. */
const BLOCK_SEC = 0.4;
const BLOCK_STEP = 0.25;

/** Short-term loudness uses three-second blocks. */
const SHORT_TERM_SEC = 3;

/** Absolute gate: nothing below this counts towards the integrated figure. */
const ABSOLUTE_GATE_LUFS = -70;
/** Relative gate: blocks more than this far below the ungated mean are dropped. */
const RELATIVE_GATE_LU = -10;

export interface LoudnessResult {
  /** Gated loudness of the whole programme, in LUFS. -Infinity for silence. */
  integratedLufs: number;
  /** Loudest 400ms block, in LUFS. */
  momentaryMaxLufs: number;
  /** Loudest 3s window, in LUFS. */
  shortTermMaxLufs: number;
  /** Spread between the 10th and 95th percentile of short-term loudness, in LU. */
  loudnessRangeLu: number;
  /** Highest inter-sample value, in dB relative to full scale. */
  truePeakDb: number;
  /** Highest sample value, in dB relative to full scale. */
  samplePeakDb: number;
}

interface Biquad {
  b0: number;
  b1: number;
  b2: number;
  a1: number;
  a2: number;
}

/**
 * The two K-weighting stages, designed for a given sample rate.
 *
 * The standard publishes coefficients for 48kHz only. Rather than resample or
 * refuse other rates, the filters are designed from the parameters those
 * coefficients came from — a high shelf at 1682Hz and a high-pass at 38Hz — so
 * the weighting is correct at any rate. A test checks that at 48kHz this
 * reproduces the published numbers.
 */
export function kWeightingFilters(sampleRate: number): [Biquad, Biquad] {
  // High shelf: +4dB above about 2kHz, standing in for the acoustic effect of a
  // head in a sound field.
  const shelfF0 = 1681.974450955533;
  const shelfGainDb = 3.999843853973347;
  const shelfQ = 0.7071752369554196;
  const k = Math.tan((Math.PI * shelfF0) / sampleRate);
  const vh = Math.pow(10, shelfGainDb / 20);
  const vb = Math.pow(vh, 0.4996667741545416);
  const shelfA0 = 1 + k / shelfQ + k * k;
  const shelf: Biquad = {
    b0: (vh + (vb * k) / shelfQ + k * k) / shelfA0,
    b1: (2 * (k * k - vh)) / shelfA0,
    b2: (vh - (vb * k) / shelfQ + k * k) / shelfA0,
    a1: (2 * (k * k - 1)) / shelfA0,
    a2: (1 - k / shelfQ + k * k) / shelfA0,
  };

  // High-pass, the "RLB" curve: removes the rumble that contributes nothing to
  // perceived loudness but plenty to a mean square.
  const hpF0 = 38.13547087602444;
  const hpQ = 0.5003270373238773;
  const kh = Math.tan((Math.PI * hpF0) / sampleRate);
  const hpA0 = 1 + kh / hpQ + kh * kh;
  const highPass: Biquad = {
    b0: 1,
    b1: -2,
    b2: 1,
    a1: (2 * (kh * kh - 1)) / hpA0,
    a2: (1 - kh / hpQ + kh * kh) / hpA0,
  };

  return [shelf, highPass];
}

/** Runs one biquad over a channel, in place, in direct form I. */
function applyBiquad(channel: Float64Array, filter: Biquad): void {
  let x1 = 0;
  let x2 = 0;
  let y1 = 0;
  let y2 = 0;
  for (let i = 0; i < channel.length; i++) {
    const x0 = channel[i];
    const y0 = filter.b0 * x0 + filter.b1 * x1 + filter.b2 * x2 - filter.a1 * y1 - filter.a2 * y2;
    x2 = x1;
    x1 = x0;
    y2 = y1;
    y1 = y0;
    channel[i] = y0;
  }
}

/** A copy of `channels`, K-weighted. */
export function kWeight(channels: readonly Float32Array[], sampleRate: number): Float64Array[] {
  const [shelf, highPass] = kWeightingFilters(sampleRate);
  return channels.map((channel) => {
    const out = Float64Array.from(channel);
    applyBiquad(out, shelf);
    applyBiquad(out, highPass);
    return out;
  });
}

/**
 * Channel weights from BS.1770. The surround channels count for more because a
 * sound arriving from behind is heard as louder than the same sound in front;
 * stereo and mono use unity throughout.
 */
function channelWeight(index: number, channelCount: number): number {
  if (channelCount <= 2) return 1;
  // 5.1 order: L R C LFE Ls Rs. The LFE is excluded entirely.
  if (index === 3) return 0;
  return index >= 4 ? 1.41 : 1;
}

/** Loudness of one block, given the mean square of each weighted channel. */
function blockLoudness(meanSquares: readonly number[], channelCount: number): number {
  let sum = 0;
  for (let c = 0; c < meanSquares.length; c++) sum += channelWeight(c, channelCount) * meanSquares[c];
  return sum <= 0 ? -Infinity : LOUDNESS_OFFSET + 10 * Math.log10(sum);
}

/** Loudness of every overlapping block of a given length, in LUFS. */
function blockLoudnesses(
  weighted: readonly Float64Array[],
  sampleRate: number,
  blockSec: number,
  stepSec: number,
): number[] {
  const blockLength = Math.round(blockSec * sampleRate);
  const step = Math.max(1, Math.round(stepSec * sampleRate));
  const length = weighted[0]?.length ?? 0;
  if (length < blockLength) return [];

  const out: number[] = [];
  for (let start = 0; start + blockLength <= length; start += step) {
    const meanSquares = weighted.map((channel) => {
      let sum = 0;
      for (let i = start; i < start + blockLength; i++) sum += channel[i] * channel[i];
      return sum / blockLength;
    });
    out.push(blockLoudness(meanSquares, weighted.length));
  }
  return out;
}

/** The mean of a set of block loudnesses, taken in the power domain. */
function meanLoudness(values: readonly number[]): number {
  if (values.length === 0) return -Infinity;
  let sum = 0;
  for (const value of values) sum += Math.pow(10, (value - LOUDNESS_OFFSET) / 10);
  return sum <= 0 ? -Infinity : LOUDNESS_OFFSET + 10 * Math.log10(sum / values.length);
}

/**
 * Integrated loudness: the mean of the blocks, twice gated.
 *
 * The absolute gate throws away silence. The relative gate — ten units below the
 * mean of what is left — throws away the quiet passages, so a piece with a long
 * fade-out is not reported as quieter than it sounds. Both gates are in the
 * standard for the same reason: without them the number tracks how much silence
 * a recording contains rather than how loud it is.
 */
export function integratedLoudness(blocks: readonly number[]): number {
  const aboveAbsolute = blocks.filter((value) => value > ABSOLUTE_GATE_LUFS);
  if (aboveAbsolute.length === 0) return -Infinity;
  const ungated = meanLoudness(aboveAbsolute);
  if (!Number.isFinite(ungated)) return -Infinity;
  const gated = aboveAbsolute.filter((value) => value > ungated + RELATIVE_GATE_LU);
  return gated.length === 0 ? ungated : meanLoudness(gated);
}

/**
 * Loudness range: the spread of the short-term loudness distribution, between
 * its 10th and 95th percentiles, after gating.
 *
 * Percentiles rather than a maximum and a minimum, because one loud crash or one
 * silent bar should not define the dynamics of a whole piece.
 */
export function loudnessRange(shortTerm: readonly number[]): number {
  const aboveAbsolute = shortTerm.filter((value) => value > ABSOLUTE_GATE_LUFS);
  if (aboveAbsolute.length === 0) return 0;
  const mean = meanLoudness(aboveAbsolute);
  const gated = aboveAbsolute.filter((value) => value > mean - 20).sort((a, b) => a - b);
  if (gated.length === 0) return 0;

  const at = (fraction: number): number => gated[Math.min(gated.length - 1, Math.round(fraction * (gated.length - 1)))];
  return Math.max(0, at(0.95) - at(0.1));
}

/**
 * Four-times oversampling, as the standard asks for: sixteen-tap windowed sinc,
 * three interpolated points between each pair of samples.
 *
 * The coefficients depend only on the phase, so they are worked out once. Doing
 * it per sample means ninety-six trigonometric calls for every sample of every
 * channel, which turned a few seconds of audio into tens of seconds of
 * arithmetic — the export was measurably waiting on it.
 */
const OVERSAMPLE_TAPS = 16;
const OVERSAMPLE_HALF = OVERSAMPLE_TAPS / 2;
const OVERSAMPLE_COEFFICIENTS: Float64Array[] = Array.from({ length: 3 }, (_, phase) => {
  const offset = (phase + 1) / 4;
  const taps = new Float64Array(OVERSAMPLE_TAPS);
  for (let k = 0; k < OVERSAMPLE_TAPS; k++) {
    const t = k - OVERSAMPLE_HALF + 1;
    const x = Math.PI * (offset - t);
    // sinc, windowed by a Hann of the same width, which keeps the estimate
    // within a fraction of a decibel of the true maximum.
    const sinc = Math.abs(x) < 1e-9 ? 1 : Math.sin(x) / x;
    const window = 0.5 + 0.5 * Math.cos((Math.PI * (offset - t)) / OVERSAMPLE_HALF);
    taps[k] = sinc * window;
  }
  return taps;
});

/**
 * The inter-sample peak magnitude at each sample position: the larger of the
 * sample itself and the reconstructed curve just after it, across all channels.
 *
 * Between two samples the reconstructed waveform can rise above both of them, so
 * a file that never exceeds -0.1 dBFS by sample can still overload a converter
 * or a lossy encoder.
 */
export function interSamplePeaks(channels: readonly Float32Array[]): Float32Array {
  const length = channels[0]?.length ?? 0;
  const out = new Float32Array(length);

  for (const channel of channels) {
    for (let i = 0; i < length; i++) {
      let peak = Math.abs(channel[i]);
      const from = i - OVERSAMPLE_HALF + 1;
      const clipped = from < 0 || from + OVERSAMPLE_TAPS > length;
      for (let phase = 0; phase < 3; phase++) {
        const taps = OVERSAMPLE_COEFFICIENTS[phase];
        let sum = 0;
        if (clipped) {
          for (let k = 0; k < OVERSAMPLE_TAPS; k++) {
            const index = from + k;
            if (index < 0 || index >= length) continue;
            sum += channel[index] * taps[k];
          }
        } else {
          for (let k = 0; k < OVERSAMPLE_TAPS; k++) sum += channel[from + k] * taps[k];
        }
        const magnitude = sum < 0 ? -sum : sum;
        if (magnitude > peak) peak = magnitude;
      }
      if (peak > out[i]) out[i] = peak;
    }
  }
  return out;
}

/** Highest inter-sample peak anywhere. */
export function truePeak(channels: readonly Float32Array[]): number {
  let peak = 0;
  for (const value of interSamplePeaks(channels)) if (value > peak) peak = value;
  return peak;
}

const toDb = (value: number): number => (value <= 0 ? -Infinity : 20 * Math.log10(value));

/** Every loudness figure for a finished piece of audio. */
export function measureLoudness(channels: readonly Float32Array[], sampleRate: number): LoudnessResult {
  if (channels.length === 0 || (channels[0]?.length ?? 0) === 0) {
    return {
      integratedLufs: -Infinity,
      momentaryMaxLufs: -Infinity,
      shortTermMaxLufs: -Infinity,
      loudnessRangeLu: 0,
      truePeakDb: -Infinity,
      samplePeakDb: -Infinity,
    };
  }

  const weighted = kWeight(channels, sampleRate);
  const momentary = blockLoudnesses(weighted, sampleRate, BLOCK_SEC, BLOCK_STEP);
  const shortTerm = blockLoudnesses(weighted, sampleRate, SHORT_TERM_SEC, 1);

  let samplePeak = 0;
  for (const channel of channels) {
    for (let i = 0; i < channel.length; i++) samplePeak = Math.max(samplePeak, Math.abs(channel[i]));
  }

  return {
    integratedLufs: integratedLoudness(momentary),
    momentaryMaxLufs: momentary.length > 0 ? Math.max(...momentary) : -Infinity,
    shortTermMaxLufs: shortTerm.length > 0 ? Math.max(...shortTerm) : -Infinity,
    loudnessRangeLu: loudnessRange(shortTerm),
    truePeakDb: toDb(truePeak(channels)),
    samplePeakDb: toDb(samplePeak),
  };
}

/**
 * The gain, in decibels, that would bring `measured` to `targetLufs`.
 *
 * Capped so the result cannot be pushed into clipping: if the true peak would go
 * past the ceiling, the gain is reduced to whatever fits. Loudness matching that
 * silently clips is worse than loudness matching that falls short, and the
 * caller is told which happened.
 */
export function loudnessMatchGainDb(
  measured: LoudnessResult,
  targetLufs: number,
  ceilingDbTp = -1,
): { gainDb: number; limitedByPeak: boolean } {
  if (!Number.isFinite(measured.integratedLufs)) return { gainDb: 0, limitedByPeak: false };
  const wanted = targetLufs - measured.integratedLufs;
  const headroom = ceilingDbTp - measured.truePeakDb;
  if (Number.isFinite(headroom) && wanted > headroom) {
    return { gainDb: headroom, limitedByPeak: true };
  }
  return { gainDb: wanted, limitedByPeak: false };
}
